import { useState, useEffect } from "react";
import useSWR from "swr";
import { api, fmtEur, fmtNum, WEEKDAYS_SK } from "@/lib/app";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Cell } from "recharts";
import { TrendUp, Lightning } from "@phosphor-icons/react";
import PlanForward from "@/components/PlanForward";

const fetcher = (url) => api.get(url).then((r) => r.data);

export default function Forecast({ storeId, range, period }) {
  // train window = period selected upstream; forecast window = next N days
  const [forecastDays, setForecastDays] = useState(14);
  const [forecastFrom, setForecastFrom] = useState("");
  const [forecastTo, setForecastTo] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    if (range?.max && !forecastFrom) {
      const start = new Date(range.max);
      start.setDate(start.getDate() + 1);
      const end = new Date(start);
      end.setDate(end.getDate() + forecastDays - 1);
      setForecastFrom(start.toISOString().slice(0, 10));
      setForecastTo(end.toISOString().slice(0, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range?.max]);

  useEffect(() => {
    if (!forecastFrom) return;
    const start = new Date(forecastFrom);
    const end = new Date(start);
    end.setDate(end.getDate() + forecastDays - 1);
    setForecastTo(end.toISOString().slice(0, 10));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecastDays, forecastFrom]);

  const qs = period.from && period.to && forecastFrom && forecastTo
    ? `period_from=${period.from}&period_to=${period.to}&forecast_from=${forecastFrom}&forecast_to=${forecastTo}`
    : null;
  const { data, error, isLoading } = useSWR(qs ? `/stores/${storeId}/analytics/forecast?${qs}` : null, fetcher);

  const selectedDay = data?.days?.find((d) => d.date === selectedDate) || data?.days?.[0];

  return (
    <div className="space-y-5">
      <PlanForward storeId={storeId} period={period} />

      <div className="card-flat p-4 flex flex-wrap items-end gap-4">
        <div>
          <p className="label-eyebrow mb-1">Trénovacie obdobie</p>
          <p className="text-sm tabular">{period.from} → {period.to}</p>
        </div>
        <div>
          <p className="label-eyebrow mb-1">Forecast od</p>
          <input
            type="date"
            value={forecastFrom}
            onChange={(e) => setForecastFrom(e.target.value)}
            data-testid="forecast-from-input"
          />
        </div>
        <div>
          <p className="label-eyebrow mb-1">Dĺžka (dní)</p>
          <input
            type="number"
            min="1"
            max="90"
            value={forecastDays}
            onChange={(e) => setForecastDays(Math.max(1, Math.min(90, parseInt(e.target.value) || 1)))}
            className="w-20 tabular"
            data-testid="forecast-days-input"
          />
        </div>
        <div className="ml-auto text-xs text-[var(--text-muted)] max-w-md">
          Lineárna regresia tržieb per (deň týždňa × hodina). Odporúčaný počet ľudí = predikovaná tržba / požadovaný výkon, s rešpektom min. obsluhy.
        </div>
      </div>

      {isLoading && <div className="text-[var(--text-secondary)]">Generujem forecast…</div>}
      {error && <div className="card-flat p-4 text-sm text-[var(--critical)]">{error.response?.data?.detail || "Chyba pri forecastingu"}</div>}

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="card-flat p-5">
              <p className="label-eyebrow mb-2">Predikované obdobie</p>
              <p className="font-display text-xl font-bold tabular">{data.forecast_period.from}</p>
              <p className="tabular text-sm text-[var(--text-secondary)]">→ {data.forecast_period.to}</p>
              <p className="text-xs text-[var(--text-muted)] mt-1">{data.forecast_period.days} dní</p>
            </div>
            <div className="card-flat p-5">
              <p className="label-eyebrow mb-2">Predikovaná tržba</p>
              <p className="font-display text-3xl font-bold tabular text-[var(--success)]">{fmtEur(data.summary.total_revenue)}</p>
              <p className="text-xs text-[var(--text-muted)] mt-1">Suma za celé obdobie</p>
            </div>
            <div className="card-flat p-5">
              <p className="label-eyebrow mb-2">Odporúčané osobohodiny</p>
              <p className="font-display text-3xl font-bold tabular">{fmtNum(data.summary.total_person_hours, 0)}</p>
              <p className="text-xs text-[var(--text-muted)] mt-1">Celkový plán</p>
            </div>
          </div>

          <div className="card-flat p-5">
            <h3 className="font-display text-base font-semibold mb-1">Denná predikovaná tržba</h3>
            <p className="text-xs text-[var(--text-secondary)] mb-4">Kliknite na stĺpec pre hodinový plán.</p>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.days} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#E4E8E5" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#5E6962" }} />
                <YAxis tick={{ fontSize: 11, fill: "#5E6962" }} tickFormatter={(v) => `${v}€`} />
                <Tooltip
                  formatter={(v) => fmtEur(v)}
                  contentStyle={{ background: "white", border: "1px solid #E4E8E5", borderRadius: 6, fontSize: 12 }}
                />
                <Bar dataKey="pred_revenue_total" name="Predikovaná tržba" radius={[3, 3, 0, 0]} isAnimationActive={false}
                     onClick={(e) => setSelectedDate(e.date)} cursor="pointer">
                  {data.days.map((d, i) => (
                    <Cell key={i} fill={selectedDay?.date === d.date ? "#1E3627" : "#2B4C38"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {selectedDay && (
            <div className="card-flat overflow-hidden">
              <div className="px-5 pt-5 pb-3 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className="font-display text-base font-semibold inline-flex items-center gap-2">
                    <Lightning size={18} weight="duotone" className="text-[var(--brand)]" />
                    Plán pre {selectedDay.weekday_name}, {selectedDay.date}
                  </h3>
                  <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                    Otvorené {selectedDay.open_from}–{selectedDay.open_to} · Predikovaná tržba {fmtEur(selectedDay.pred_revenue_total)} · {fmtNum(selectedDay.pred_person_hours, 1)} osobohodín
                  </p>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {data.days.map((d) => (
                    <button
                      key={d.date}
                      onClick={() => setSelectedDate(d.date)}
                      data-testid={`forecast-day-${d.date}`}
                      className={`px-2 py-1 text-[10px] rounded tabular ${
                        selectedDay.date === d.date ? "bg-[var(--brand)] text-white" : "btn-ghost"
                      }`}
                    >
                      {d.date.slice(5)} {d.weekday_name.slice(0, 2)}
                    </button>
                  ))}
                </div>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>Hodina</th>
                    <th className="text-right">Pred. tržba</th>
                    <th className="text-right">Odporúčané os.</th>
                    <th className="text-right">Min. ráno/večer</th>
                    <th className="text-right">Historický ø</th>
                    <th className="text-right">Δ vs história</th>
                    <th className="text-right">Vzoriek</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedDay.hours.map((h) => {
                    const delta = h.delta_vs_hist;
                    const tone =
                      Math.abs(delta) < 0.3 ? "" : delta > 0 ? "text-[var(--critical)]" : "text-[var(--success)]";
                    return (
                      <tr key={h.hour}>
                        <td className="tabular">{h.hour}-{h.hour + 1}</td>
                        <td className="text-right tabular">{fmtEur(h.pred_revenue)}</td>
                        <td className="text-right tabular font-medium">{fmtNum(h.recommended_employees, 1)}</td>
                        <td className="text-right tabular text-[var(--text-muted)]">
                          {h.min_required > 0 ? fmtNum(h.min_required, 0) : "—"}
                        </td>
                        <td className="text-right tabular text-[var(--text-secondary)]">{fmtNum(h.hist_avg_employees, 1)}</td>
                        <td className={`text-right tabular ${tone}`}>
                          {delta > 0 ? "+" : ""}{fmtNum(delta, 2)}
                        </td>
                        <td className="text-right tabular text-[var(--text-muted)]">{h.n_samples}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="p-3 text-xs text-[var(--text-muted)] border-t border-[var(--border-default)] bg-[var(--bg-page)]">
                <strong>Δ vs história</strong>: Záporné = pri minulom personále bola priemerná tržba nižšia, môžete znížiť počet ľudí. Kladné = forecast indikuje potrebu pridať osoby.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
